import { useState, useEffect } from "react";
import { sanitarywareService } from "../../../services/enhancedCrudService";
import { toast } from "react-toastify";

const SanitarywareAttributes = () => {
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    type: "text",
    options: "",
    required: false,
    status: "active",
  });

  const fetchAttributes = async () => {
    try {
      setLoading(true);
      const response = await sanitarywareService.getAttributes();
      setAttributes(response.data || []);
    } catch (error) {
      console.error("Error fetching attributes:", error);
      toast.error("Failed to load attributes");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        options:
          formData.type === "select"
            ? formData.options.split(",").map((opt) => opt.trim())
            : [],
      };

      if (editing) {
        await sanitarywareService.updateAttribute(editing.id, submitData);
        toast.success("Attribute updated successfully");
      } else {
        await sanitarywareService.createAttribute(submitData);
        toast.success("Attribute created successfully");
      }

      setEditing(null);
      setFormData({
        name: "",
        type: "text",
        options: "",
        required: false,
        status: "active",
      });
      fetchAttributes();
    } catch (error) {
      console.error("Error saving attribute:", error);
      toast.error("Failed to save attribute");
    }
  };

  const handleEdit = (attribute) => {
    setEditing(attribute);
    setFormData({
      name: attribute.name,
      type: attribute.type,
      options: attribute.options ? attribute.options.join(", ") : "",
      required: attribute.required,
      status: attribute.status,
    });
  };

  const handleCancel = () => {
    setEditing(null);
    setFormData({
      name: "",
      type: "text",
      options: "",
      required: false,
      status: "active",
    });
  };

  const handleDelete = async (attributeId) => {
    if (window.confirm("Are you sure you want to delete this attribute?")) {
      try {
        await sanitarywareService.deleteAttribute(attributeId);
        toast.success("Attribute deleted successfully");
        fetchAttributes();
      } catch (error) {
        console.error("Error deleting attribute:", error);
        toast.error("Failed to delete attribute");
      }
    }
  };

  useEffect(() => {
    fetchAttributes();
  }, []);

  if (loading) {
    return (
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <div className="text-center">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-md-12">
        <div className="card">
          <div className="card-header">
            <h4 className="card-title">Sanitaryware Attributes</h4>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-4">
                <div className="card">
                  <div className="card-header">
                    <h5>{editing ? "Edit Attribute" : "Add New Attribute"}</h5>
                  </div>
                  <div className="card-body">
                    <form onSubmit={handleSubmit}>
                      <div className="form-group">
                        <label>Attribute Name *</label>
                        <input
                          type="text"
                          className="form-control"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Type *</label>
                        <select
                          className="form-control"
                          name="type"
                          value={formData.type}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="text">Text</option>
                          <option value="number">Number</option>
                          <option value="select">Select</option>
                          <option value="textarea">Textarea</option>
                          <option value="boolean">Boolean</option>
                        </select>
                      </div>
                      {formData.type === "select" && (
                        <div className="form-group">
                          <label>Options *</label>
                          <textarea
                            className="form-control"
                            name="options"
                            value={formData.options}
                            onChange={handleInputChange}
                            rows="3"
                            placeholder="Enter options separated by commas"
                            required
                          />
                          <small className="form-text text-muted">
                            Separate multiple options with commas (e.g., Red,
                            Blue, Green)
                          </small>
                        </div>
                      )}
                      <div className="form-group">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            name="required"
                            checked={formData.required}
                            onChange={handleInputChange}
                          />
                          <label className="form-check-label">
                            Required field
                          </label>
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Status</label>
                        <select
                          className="form-control"
                          name="status"
                          value={formData.status}
                          onChange={handleInputChange}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <button type="submit" className="btn btn-primary">
                          {editing ? "Update Attribute" : "Add Attribute"}
                        </button>
                        {editing && (
                          <button
                            type="button"
                            className="btn btn-secondary ml-2"
                            onClick={handleCancel}
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </form>
                  </div>
                </div>
              </div>

              <div className="col-md-8">
                {/* Attributes List */}
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Type</th>
                        <th>Options</th>
                        <th>Required</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attributes.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center">
                            No attributes found
                          </td>
                        </tr>
                      ) : (
                        attributes.map((attribute) => (
                          <tr key={attribute.id}>
                            <td>{attribute.id}</td>
                            <td>
                              <strong>{attribute.name}</strong>
                            </td>
                            <td>
                              <span className="badge badge-info">
                                {attribute.type}
                              </span>
                            </td>
                            <td>
                              {attribute.type === "select" &&
                              attribute.options ? (
                                <div style={{ maxWidth: "200px" }}>
                                  {attribute.options.join(", ")}
                                </div>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              <span
                                className={`badge badge-${
                                  attribute.required ? "success" : "secondary"
                                }`}
                              >
                                {attribute.required ? "Yes" : "No"}
                              </span>
                            </td>
                            <td>
                              <span
                                className={`badge badge-${
                                  attribute.status === "active"
                                    ? "success"
                                    : "danger"
                                }`}
                              >
                                {attribute.status}
                              </span>
                            </td>
                            <td>
                              <div className="btn-group" role="group">
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => handleEdit(attribute)}
                                  title="Edit"
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => handleDelete(attribute.id)}
                                  title="Delete"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="row mt-4">
                  <div className="col-md-3">
                    <div className="card bg-primary text-white">
                      <div className="card-body text-center">
                        <h4>{attributes.length}</h4>
                        <p className="mb-0">Total Attributes</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-success text-white">
                      <div className="card-body text-center">
                        <h4>
                          {
                            attributes.filter((a) => a.status === "active")
                              .length
                          }
                        </h4>
                        <p className="mb-0">Active Attributes</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-info text-white">
                      <div className="card-body text-center">
                        <h4>
                          {attributes.filter((a) => a.type === "select").length}
                        </h4>
                        <p className="mb-0">Select Attributes</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-warning text-white">
                      <div className="card-body text-center">
                        <h4>{attributes.filter((a) => a.required).length}</h4>
                        <p className="mb-0">Required Fields</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SanitarywareAttributes;
